import { Component, EventEmitter, Input, OnInit, Output, signal, DestroyRef } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { BehaviorSubject } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, distinctUntilChanged, filter as rxFilter, switchMap, tap, finalize } from 'rxjs/operators';
import { ISelectOption } from 'src/app/models/selectOptions';
import { ISelectOptionGroups } from 'src/app/models/selectOptions/selectOptionGroups.interface';
import { FilterTypesEnum } from 'src/app/models/filters/filterTypes.enum';
import { InventoryHttpService } from 'src/app/services/httpServices/inventoryHttpService/inventory-http.service';
import { AutocompleteSearchResultsModel } from 'src/app/models/api/autocompleteSearchResults.model';
import { AuditLogEventsFiltersModel } from 'src/app/models/filters/implementations/pageFilters/AuditLogEventsFilters.model';
import { AccountManagementHttpService } from 'src/app/services/httpServices/accountManagementHttpService/account-management-http-service.service';
@Component({
  selector: 'app-audit-log-event-vertical-filters',
  templateUrl: './audit-log-event-vertical-filters.component.html',
  styleUrls: ['./audit-log-event-vertical-filters.component.scss'],
})
export class AuditLogEventVerticalFiltersComponent implements OnInit {
  @Input() public pageFilters: AuditLogEventsFiltersModel;
  @Input() public isFiltersMenuOpen = true;
  @Output() public filtersOnMenuStateChange = new EventEmitter<boolean>();
  @Output() public optionOnSelection = new EventEmitter<ISelectOption[]>();

  public optionsInsideCategories$ = new BehaviorSubject<ISelectOptionGroups[]>([]);

  private readonly _optionGroups = signal<ISelectOptionGroups[]>([]);
  private readonly _expandedCategories = signal<{ [optionGroup: string]: boolean }>({});
  private readonly _selectedIds = signal<Set<string>>(new Set());
  private readonly _isOpen = signal<boolean>(true);

  public searchObjectIdentifierValuesCtrl = new UntypedFormControl();
  public filteredObjectIdentifierValues: ISelectOption[] = [];
  public isObjectIdentifierLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public objectIdentifierFilterEnum = FilterTypesEnum.ObjectIdentifier;
  public userFilterEnum = FilterTypesEnum.User;

  public objectIdentifierCtrl = new UntypedFormControl('');

  private _usersLoaded = false;

  constructor(
    private readonly _inventoryHttpService: InventoryHttpService,
    private readonly _destroyRef: DestroyRef,
    private readonly _accountHttp: AccountManagementHttpService
  ) {}

  public get expandedCategories(): { [optionGroup: string]: boolean } {
    return this._expandedCategories();
  }

  public get selectedIds(): Set<string> {
    return this._selectedIds();
  }

  public get isOpen(): boolean {
    return this._isOpen();
  }

  ngOnInit(): void {
    this.pageFilters
      .getAllFiltersOptions()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((allOptions: ISelectOption[]) => {
        const groups: ISelectOptionGroups[] = [];
        const idsByGroup: Record<string, Set<string>> = {};

        const anyFilters = this.pageFilters as unknown;
        const objFilters = this._safeGetMultiSelect(anyFilters, 'objectTypeFilters');
        const otOptions: ISelectOption[] = objFilters?.getOptions$()?.value ?? [];
        if (otOptions.length) {
          groups.push({ displayName: 'Object Type', options: otOptions.slice(), selectedCount: 0 });
          idsByGroup['Object Type'] = new Set(otOptions.map((o) => o.id));
        }

        /*const actFilters = this._safeGetMultiSelect(anyFilters, 'actionFilters');
        const actionOptions: ISelectOption[] = actFilters?.getOptions$()?.value ?? [];
        if (actionOptions.length) {
          groups.push({ displayName: 'Action', options: actionOptions.slice(), selectedCount: 0 });
          idsByGroup['Action'] = new Set(actionOptions.map((o) => o.id));
        }*/

        const byCategory: Record<string, ISelectOption[]> = {};
        allOptions.forEach((o) => {
          const catFromName = (o.categoryName as unknown as string) || '';
          const catFromType = (o.filterType as unknown as string) || '';
          const rawCategory = catFromName || catFromType;
          if (!rawCategory) return;
          if (rawCategory === (FilterTypesEnum.ObjectIdentifier as unknown as string)) {
            return;
          }
          const displayCat = this._normalizeCategoryDisplay(rawCategory);
          if (displayCat === 'Object Type') {
            if (!idsByGroup['Object Type']) idsByGroup['Object Type'] = new Set<string>();
            if (!idsByGroup['Object Type'].has(o.id)) {
              idsByGroup['Object Type'].add(o.id);
              const grp = groups.find((g) => g.displayName === 'Object Type');
              if (grp) grp.options.push(o);
            }
            return;
          }
          if (displayCat === 'Action') {
            if (!idsByGroup['Action']) idsByGroup['Action'] = new Set<string>();
            if (!idsByGroup['Action'].has(o.id)) {
              idsByGroup['Action'].add(o.id);
              const grp = groups.find((g) => g.displayName === 'Action');
              if (grp) grp.options.push(o);
            }
            return;
          }
          if (displayCat === 'User') {
            return;
          }

          if (!byCategory[displayCat]) {
            byCategory[displayCat] = [];
          }
          byCategory[displayCat].push(o);
        });

        Object.keys(byCategory).forEach((cat) => {
          const opts = byCategory[cat];
          if (opts && opts.length) {
            groups.push({ displayName: cat, options: opts, selectedCount: 0 });
          }
        });
        this._loadUsersGroup(groups);

        this.optionsInsideCategories$.next(groups);
        this._optionGroups.set(groups);

        const newSelected = new Set<string>();
        this.pageFilters.getAllSelectedOptions().forEach((opt) => newSelected.add(opt.id));
        this._selectedIds.set(newSelected);
      });

    this.pageFilters.onUpdate$.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(() => {
      const newSelected = new Set<string>();
      this.pageFilters.getAllSelectedOptions().forEach((opt) => newSelected.add(opt.id));
      this._selectedIds.set(newSelected);
    });

    this.objectIdentifierCtrl.valueChanges
      .pipe(debounceTime(400), distinctUntilChanged(), takeUntilDestroyed(this._destroyRef))
      .subscribe((val) => {
        this.pageFilters.updateFilterFromUserSelection(FilterTypesEnum.ObjectIdentifier, val as string);
      });

    this._initFilter(
      this.searchObjectIdentifierValuesCtrl,
      'objectIdentifier',
      this.filteredObjectIdentifierValues,
      this.isObjectIdentifierLoading$,
      FilterTypesEnum.ObjectIdentifier
    );
  }

  public objectIdentifierSubmitted(values: ISelectOption[] = []): void {
    this._submitSelect(values, FilterTypesEnum.ObjectIdentifier);
  }

  public onObjectIdentifierEnter(): void {
    const val = (this.objectIdentifierCtrl.value || '').toString().trim();
    if (!val) return;
    const option: ISelectOption = {
      id: val,
      value: val,
      displayName: val,
      filterType: this.objectIdentifierFilterEnum,
      categoryName: this.objectIdentifierFilterEnum,
      withoutBeautifyDisplayName: true,
    } as unknown as ISelectOption;
    this.objectIdentifierSubmitted([option]);
  }

  public expandClicked(optionGroupDisplayName: string, status: boolean) {
    this._expandedCategories.update((prev) => ({ ...prev, [optionGroupDisplayName]: status }));
  }

  public onToggleClick() {
    this.filtersOnMenuStateChange.emit(!this.isFiltersMenuOpen);
    this._isOpen.update((v) => !v);
  }

  public onSelect(id: string): void {
    this._selectedIds.update((prev) => {
      const next = new Set(prev);
      if (!next.has(id)) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });

    const selectedOptions = this._getSelectedOptions();
    this.optionOnSelection.emit(selectedOptions);
  }

  private _submitSelect(values: ISelectOption[] = [], filter: FilterTypesEnum): void {
    const first = values[0];
    const text = first?.displayName ?? first?.value ?? '';

    this.pageFilters.updateFilterFromUserSelection(filter, text);
    const add = (this.pageFilters as Partial<{ addAutocompleteOptions: (opts: ISelectOption[]) => void }>)
      .addAutocompleteOptions;
    if (add && values.length) add.call(this.pageFilters, values);
  }

  private _initFilter(
    control: UntypedFormControl,
    filter_name: string,
    filteredValues: ISelectOption[],
    loadingSubject: BehaviorSubject<boolean>,
    filterType: FilterTypesEnum
  ) {
    control.valueChanges
      .pipe(
        rxFilter((res) => res !== null && res.length >= 3),
        distinctUntilChanged(),
        debounceTime(500),
        tap(() => {
          filteredValues.splice(0, filteredValues.length);
          loadingSubject.next(true);
        }),
        switchMap((value) =>
          this._inventoryHttpService.getAutocompleteOptions(value, filter_name).pipe(
            finalize(() => {
              loadingSubject.next(false);
            })
          )
        ),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe((data: AutocompleteSearchResultsModel[]) => {
        if (data.length === 0) {
          filteredValues.splice(0, filteredValues.length);
        } else {
          filteredValues.splice(
            0,
            filteredValues.length,
            ...data.map(
              (item) =>
                ({
                  id: item.id,
                  value: item.id,
                  displayName: item.displayName,
                  filterType: filterType,
                  categoryName: filterType,
                  withoutBeautifyDisplayName: true,
                }) as ISelectOption
            )
          );
        }
      });
  }

  private _safeGetMultiSelect(
    src: unknown,
    key: string
  ): { getOptions$: () => BehaviorSubject<ISelectOption[]> } | null {
    if (!src || typeof src !== 'object') return null;

    const candidate = (src as Record<string, unknown>)[key];
    if (!candidate || typeof candidate !== 'object') return null;

    const fn = (candidate as { getOptions$?: () => BehaviorSubject<ISelectOption[]> }).getOptions$;
    return typeof fn === 'function' ? { getOptions$: fn.bind(candidate) } : null;
  }

  private _normalizeCategoryDisplay(category: string): string {
    if (category === 'ObjectType' || category === FilterTypesEnum.ResourceType) {
      return 'Object Type';
    }
    if (
      category === 'Execution Status' ||
      category === FilterTypesEnum.ExecutionStatus ||
      category === 'Exempted' ||
      category === FilterTypesEnum.Exempted
    ) {
      return 'Action';
    }
    return category;
  }

  private _getSelectedOptions(): ISelectOption[] {
    const selectedIndexArray = Array.from(this._selectedIds());

    const allOptions: Record<string, ISelectOption> = {};
    this.optionsInsideCategories$.value.forEach((g) => {
      (g.options || []).forEach((o) => (allOptions[o.id] = o));
    });

    const selectedOptions = selectedIndexArray.map((id) => allOptions[id]).filter((o) => !!o);
    return selectedOptions;
  }

  private _loadUsersGroup(groups: ISelectOptionGroups[]): void {
    this._accountHttp
      .getUsersWithExpandOption('none')
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((users) => {
        const userOptions: ISelectOption[] = (users || []).map((u) => {
          const display = u?.name && u.name.trim().length > 0 ? `${u.name.split(' ')[0]} (${u.email})` : u.email;
          return {
            id: u.email,
            value: u.email,
            displayName: display,
            filterType: this.userFilterEnum,
            categoryName: this.userFilterEnum,
            withoutBeautifyDisplayName: true,
          } as ISelectOption;
        });
        // deduplicate by id to avoid duplicate track keys
        const seen = new Set<string>();
        const uniqueUsers = userOptions.filter((o) => {
          if (seen.has(o.id)) return false;
          seen.add(o.id);
          return true;
        });
        if (!groups.find((g) => g.displayName === 'User')) {
          groups.push({ displayName: 'User', options: uniqueUsers, selectedCount: 0 });
        }
        this.optionsInsideCategories$.next(groups);
        this._optionGroups.set(groups);

        if (!this._usersLoaded) {
          const objectTypeOptions = this.pageFilters.objectTypeFilters.getOptions$().value;
          const actionOptions = this.pageFilters.actionFilters.getOptions$().value;
          this.pageFilters.updateOptions(objectTypeOptions, actionOptions, uniqueUsers);
          this._usersLoaded = true;
        }
      });
  }
}
